/**
 * Pure helpers for tab strip reordering. `resolveDropIndex` converts a drop on tab `over`
 * (before or after its midpoint) into the target index expected by a move(from, to) that first
 * removes `from` and then inserts at `to` in the shortened list.
 */
export type DropSide = 'before' | 'after';

export function resolveDropIndex(from: number, over: number, side: DropSide): number {
  if (side === 'before') return from < over ? over - 1 : over;
  return from < over ? over : over + 1;
}

/** Applies move(from, to) with the same semantics as the layout store's moveTab. */
export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const next = items.slice();
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return next;
  next.splice(Math.max(0, Math.min(next.length, to)), 0, moved);
  return next;
}

export interface TabRect {
  left: number;
  right: number;
}

/**
 * Pointer-drag hit test: which tab a pointer at `clientX` is over and on which side of its
 * midpoint. Outside the strip it snaps to the nearest end (before the first, after the last).
 * Returns null for an empty strip.
 */
export function resolvePointerDrop(rects: readonly TabRect[], clientX: number): { index: number; side: DropSide } | null {
  if (rects.length === 0) return null;
  for (let i = 0; i < rects.length; i += 1) {
    const r = rects[i]!;
    if (clientX >= r.left && clientX < r.right) return { index: i, side: clientX < r.left + (r.right - r.left) / 2 ? 'before' : 'after' };
  }
  const first = rects[0]!;
  if (clientX < first.left) return { index: 0, side: 'before' };
  return { index: rects.length - 1, side: 'after' };
}

/** Distance in px the pointer must travel before a press becomes a drag (a plain click selects). */
export const TAB_DRAG_THRESHOLD_PX = 4;

/**
 * Scroll offset that brings a tab fully into the visible part of the strip with the least movement:
 * unchanged when the tab is already in view, otherwise aligned to the nearer edge. `strip` and `tab`
 * are viewport rects (getBoundingClientRect), `scrollLeft` the strip's current offset.
 */
export function resolveRevealScroll(strip: TabRect, tab: TabRect, scrollLeft: number): number {
  if (tab.left < strip.left) return Math.max(0, scrollLeft - (strip.left - tab.left));
  if (tab.right > strip.right) return scrollLeft + (tab.right - strip.right);
  return scrollLeft;
}
